import React from 'react';
import { 
  Bot, 
  Zap, 
  Clock, 
  Sparkles, 
  CheckCircle2, 
  ArrowLeft, 
  ArrowRight, 
  ShieldCheck, 
  MessageSquare, 
  Smartphone,
  Gauge,
  HelpCircle,
  TrendingDown
} from 'lucide-react';
import { Language } from '../types';

interface InstantAiFestivalBannerProps {
  lang: Language;
  onOpenCalculator?: (presetTech?: string) => void;
  onOpenContact?: () => void;
}

export const InstantAiFestivalBanner: React.FC<InstantAiFestivalBannerProps> = ({
  lang,
  onOpenCalculator,
  onOpenContact,
}) => {
  const isFa = lang === 'fa';
  const ArrowIcon = isFa ? ArrowLeft : ArrowRight;

  const originalPriceText = isFa ? '۱۰ میلیون تومان' : '$300 USD';
  const discountText = isFa ? '۷۰٪ تخفیف (-۷ میلیون)' : '70% OFF (-$210 USD)';
  const festivalPriceText = isFa ? 'از ۳ میلیون تومان' : 'From $90 USD';
  const deliveryText = isFa ? 'تحویل تضمینی ۱ هفته (۷ روز)' : 'Guaranteed 1-Week (7 Days)';

  const waMessage = isFa
    ? `سلام جناب مهندس کاویانی‌پور، متقاضی ثبت‌نام در جشنواره «طراحی سایت فوری با هوش مصنوعی» (هزینه ویژه از ۳ میلیون تومان / تحویل ۱ هفته) از سایت sorena-it.ir هستم. لطفاً مراحل شروع پروژه را بفرمایید.`
    : `Hello Eng. Arash Kavianipoor, I would like to order the «Instant AI Web Design Festival» package (Starting from $90 USD / 1-Week Delivery) from sorena-it.ir. Please guide me to get started.`;

  const waUrl = `https://wa.me/989128634908?text=${encodeURIComponent(waMessage)}`;

  return (
    <div id="ai-festival-section" className="relative overflow-hidden rounded-3xl bg-gradient-to-b from-[#1f0f07] via-[#160904] to-[#0d0502] border-2 border-amber-500/60 shadow-2xl shadow-amber-950/60 p-6 sm:p-10 text-stone-200">
      
      {/* Ambient background glow */}
      <div className="absolute top-0 right-1/4 w-80 h-80 bg-amber-500/15 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-80 h-80 bg-yellow-500/10 rounded-full blur-[110px] pointer-events-none" />

      {/* Ribbon Banner */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-amber-500/20 via-yellow-400/20 to-amber-500/20 border border-amber-400/50 text-amber-300 text-xs sm:text-sm font-black">
          <Zap className="w-4 h-4 text-yellow-400 animate-pulse" />
          <span>
            {isFa ? 'جشنواره ویژه طراحی سایت فوری با هوش مصنوعی' : 'Instant AI Web Design Festival'}
          </span>
        </div>

        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-amber-950/80 border border-amber-700/60 text-yellow-300 text-xs font-mono font-bold">
          <Clock className="w-3.5 h-3.5 text-amber-400" />
          <span>{deliveryText}</span>
        </div>
      </div>

      {/* Main Grid: Headline & Pricing */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        
        {/* Left / Top (Text & Features) - Col 7 */}
        <div className="lg:col-span-7 space-y-5">
          <div className="space-y-2">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-white leading-tight">
              {isFa ? (
                <>
                  طراحی سایت فوری با هوش مصنوعی،{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500">
                    از ۳ میلیون تومان!
                  </span>
                </>
              ) : (
                <>
                  Instant AI-Powered Web Design,{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500">
                    From $90 USD!
                  </span>
                </>
              )}
            </h3>

            <p className="text-xs sm:text-sm md:text-base text-stone-300 leading-relaxed">
              {isFa
                ? 'وب‌سایتی شیک، مدرن و سبک با استانداردهای گوگل و سرعت خیره‌کننده، کدنویسی شده به کمک هوش مصنوعی اختصاصی سورنا آی‌تی؛ مناسب برای معرفی خدمات، لندینگ پیج، شرکت‌های نوپا و رزومه آنلاین با تحویل ۱ هفته‌ای.'
                : 'Sleek, modern, and lightning-fast website engineered with Sorena IT’s specialized AI pipeline. Perfect for business landing pages, services showcase, and startups with guaranteed 7-day turnaround.'}
            </p>
          </div>

          {/* Features Checkmarks */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2">
            {[
              {
                fa: 'تحویل تضمینی در ۷ روز کاری (۱ هفته)',
                en: 'Guaranteed 7-day turnaround (1 week)',
                icon: Clock
              },
              {
                fa: 'کدنویسی سبک و سرعت ۱۰۰ Core Web Vitals',
                en: 'Clean code with 100/100 Core Web Vitals',
                icon: Gauge
              },
              {
                fa: 'سازگاری ۱۰۰٪ با موبایل، تبلت و دسکتاپ',
                en: '100% Mobile & tablet responsive layout',
                icon: Smartphone
              },
              {
                fa: 'اتصال مستقیم به فرم، چت و واتس‌اپ',
                en: 'Direct WhatsApp & contact form integration',
                icon: MessageSquare
              },
              {
                fa: 'هاست و دامنه هدیه ۱ ساله یا اتصال دامنه',
                en: 'Hosting & domain included / custom domain',
                icon: Sparkles
              },
              {
                fa: 'پشتیبانی فنی و سئو تکنیکال اولیه',
                en: 'Full technical support & basic SEO indexing',
                icon: ShieldCheck
              },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="flex items-center gap-2 text-xs text-stone-200">
                  <CheckCircle2 className="w-4 h-4 text-yellow-400 shrink-0" />
                  <span>{isFa ? item.fa : item.en}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right / Bottom (Pricing Badge & Action Card) - Col 5 */}
        <div className="lg:col-span-5">
          <div className="rounded-2xl bg-gradient-to-b from-[#241309] to-[#140a05] border border-amber-600/70 p-6 sm:p-7 shadow-xl space-y-5 text-center">
            
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold border border-amber-500/40">
              <Bot className="w-4 h-4 text-amber-400" />
              <span>{isFa ? 'تعرفه استثنایی جشنواره هوش مصنوعی' : 'AI Festival Special Tariff'}</span>
            </div>

            {/* Pricing Details */}
            <div className="space-y-2 py-2">
              <div className="text-xs text-stone-400 line-through">
                {isFa ? 'تعرفه استاندارد: ۱۰,۰۰۰,۰۰۰ تومان' : 'Regular Price: $300 USD'}
              </div>
              
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-red-950/80 border border-red-700/60 text-red-300 text-xs font-bold">
                <TrendingDown className="w-3.5 h-3.5" />
                <span>{discountText}</span>
              </div>

              <div className="pt-2">
                <div className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 tracking-tight">
                  {festivalPriceText}
                </div>
              </div>

              {/* Delivery Tag */}
              <div className="pt-2">
                <span className="inline-block px-3 py-1 rounded-lg bg-[#34180b] text-yellow-300 text-xs font-bold border border-amber-700/60">
                  ⚡ {isFa ? 'تحویل سریع: ۷ روز کاری' : 'Express Delivery: 7 Business Days'}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2.5 pt-2">
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600 hover:from-amber-400 hover:to-yellow-300 text-stone-950 font-black text-sm flex items-center justify-center gap-2 shadow-lg shadow-amber-500/25 transition-all cursor-pointer"
              >
                <MessageSquare className="w-4 h-4 text-stone-950" />
                <span>
                  {isFa 
                    ? 'ثبت سفارش در واتس‌اپ' 
                    : 'Order on WhatsApp'}
                </span>
              </a>

              {onOpenCalculator && (
                <button
                  type="button"
                  onClick={() => onOpenCalculator('ai-instant')}
                  className="w-full py-2.5 px-4 rounded-xl bg-[#1c0d06] hover:bg-[#2c1409] text-amber-200 border border-amber-800/60 text-xs font-semibold flex items-center justify-center gap-2 transition-all cursor-pointer"
                >
                  <span>{isFa ? 'محاسبه در فرم آنلاین' : 'View in Cost Calculator'}</span>
                  <ArrowIcon className="w-3.5 h-3.5 text-amber-400" />
                </button>
              )}
            </div>

          </div>
        </div>

      </div>

    </div>
  );
};
